import React, { Component, createRef } from 'react';
import { GanttChartHeaders } from './components/GanttChartHeaders/GanttChartHeaders';
import DataViewPort from './components/GanttChartDataTaskViewPort/GanttChartDataViewPort';
import { DAY_WIDTH, FIRST_COLUMN_GAP } from './GanttChartConstants';
import { getDayOffset } from '../../slices/marketplace/timeline/utils/timeline-dates';
import GanttChartDataController from './controllers/GanttChartDataController';
import Config from './helpers/config/GanttChartConfigs';
import GanttChartDateHelper from './helpers/GanttChartDateHelper';
import './GanttChart.scss';
import { GanttChartItem } from './models/GanttChartItem.model';

interface GanttChartProps {
    itemheight: number;
    data: GanttChartItem[];
    startDate: Date;
    endDate: Date;
    onHorizonChange?: (lowerLimit: Date, upLimit: Date) => void;
    config: any;
    onTaskClick(item: GanttChartItem): void;
    displayCurrentDayIndicator: boolean;
    isTaskClickable?: (taskId: string) => boolean;
    focusEventId: string;
    currentDate?: Date;
}

interface GanttChartState {
    nowposition: number;
    scrollLeft: number;
    size: { width: number; height: number };
}

class GanttChart extends Component<GanttChartProps, GanttChartState> {
    private dc: GanttChartDataController;
    private initialise: boolean;
    private scrollContainerRef = createRef<HTMLDivElement>();
    private resizeTimeout: NodeJS.Timeout | null = null;
    private eventFocusTimeout: NodeJS.Timeout | null = null;

    constructor(props: GanttChartProps) {
        super(props);
        this.dc = new GanttChartDataController();
        this.dc.onHorizonChange = this.onHorizonChange;
        this.initialise = false;

        Config.load(this.props.config);

        const daysFromStart = Math.max(
            0,
            getDayOffset(new Date(), this.props.startDate)
        );

        this.state = {
            nowposition: daysFromStart * DAY_WIDTH,
            scrollLeft: 0,
            size: { width: 1, height: 1 },
        };
    }

    updateContainerSize = () => {
        if (this.scrollContainerRef.current) {
            if (this.resizeTimeout) {
                clearTimeout(this.resizeTimeout);
            }

            this.resizeTimeout = setTimeout(() => {
                const { clientWidth, clientHeight } =
                    this.scrollContainerRef.current!;
                this.setState({
                    size: { width: clientWidth, height: clientHeight },
                });
            }, 50);
        }
    };

    componentDidMount() {
        if (this.scrollContainerRef.current) {
            this.scrollContainerRef.current.addEventListener(
                'scroll',
                this.handleScroll
            );
            this.updateContainerSize();
            window.addEventListener('resize', this.updateContainerSize);

            if (this.eventFocusTimeout) {
                clearTimeout(this.eventFocusTimeout);
            }
            this.eventFocusTimeout = setTimeout(() => {
                if (!this.scrollContainerRef.current) {
                    return;
                }
                const containerWidth =
                    this.scrollContainerRef.current.clientWidth;

                const daysFromStart = Math.max(
                    0,
                    getDayOffset(
                        this.props.currentDate ?? new Date(),
                        this.props.startDate
                    )
                );
                const currentDayOffset =
                    daysFromStart * DAY_WIDTH + FIRST_COLUMN_GAP * DAY_WIDTH;
                const scrollLeft = Math.max(
                    0,
                    currentDayOffset - containerWidth / 2
                );

                const elementId = `event-${this.props.focusEventId}`;
                const targetEventNumber = this.props.data
                    .filter((f) => f.type === 'line')
                    .findIndex((f) => f.id === elementId);
                const scrollTop =
                    targetEventNumber >= 0
                        ? this.props.itemheight * targetEventNumber
                        : 0;

                this.scrollContainerRef.current.scrollTo({
                    left: scrollLeft,
                    top: scrollTop,
                    behavior: 'auto',
                });
            }, 10);
        }
        setTimeout(this.updateContainerSize, 0);
    }

    componentWillUnmount() {
        if (this.scrollContainerRef.current) {
            this.scrollContainerRef.current.removeEventListener(
                'scroll',
                this.handleScroll
            );
        }
        window.removeEventListener('resize', this.updateContainerSize);

        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }

        if (this.eventFocusTimeout) {
            clearTimeout(this.eventFocusTimeout);
        }
    }

    handleScroll = () => {
        if (this.scrollContainerRef.current) {
            this.setState(
                { scrollLeft: this.scrollContainerRef.current.scrollLeft },
                this.setStartEnd
            );
        }
    };

    ////////////////////
    //     ON SIZE    //
    ////////////////////

    onSize = (size: { width: number; height: number }) => {
        if (!this.initialise) {
            this.dc.initialise(
                this.state.scrollLeft + this.state.nowposition,
                this.state.scrollLeft + this.state.nowposition + size.width,
                this.state.nowposition,
                DAY_WIDTH
            );
            this.initialise = true;
        }
        this.setStartEnd();
        this.setState({
            size: size,
        });
    };

    setStartEnd = () => {
        this.dc.setStartEnd(
            this.state.scrollLeft,
            this.state.scrollLeft + this.state.size.width,
            this.state.nowposition,
            DAY_WIDTH
        );
    };

    onHorizonChange = (lowerLimit: Date, upLimit: Date) => {
        if (this.props.onHorizonChange)
            this.props.onHorizonChange(lowerLimit, upLimit);
    };
    /////////////////////
    //   ITEMS EVENTS  //
    /////////////////////

    calcNumVisibleDays = (size: { width: number; height: number }) => {
        return Math.ceil(size.width / DAY_WIDTH);
    };

    render() {
        return (
            <div
                className="gantt-chart gantt-chart-container"
                ref={this.scrollContainerRef}
                style={{ overflowX: 'auto' }}>
                <div className="timeLine-main">
                    <GanttChartHeaders
                        nowposition={this.state.nowposition}
                        upperBoundary={
                            GanttChartDateHelper.getDifferenceInDays(
                                this.props.startDate,
                                this.props.endDate
                            ) *
                                DAY_WIDTH +
                            FIRST_COLUMN_GAP * DAY_WIDTH
                        }
                        startDate={this.props.startDate}
                        daysNumber={GanttChartDateHelper.getDifferenceInDays(
                            this.props.startDate,
                            this.props.endDate
                        )}
                    />
                    <DataViewPort
                        itemheight={this.props.itemheight}
                        nowposition={this.state.nowposition}
                        data={this.props.data}
                        onTaskClick={this.props.onTaskClick}
                        upperBoundary={
                            GanttChartDateHelper.getDifferenceInDays(
                                this.props.startDate,
                                this.props.endDate
                            ) *
                                DAY_WIDTH +
                            FIRST_COLUMN_GAP * DAY_WIDTH
                        }
                        onSize={this.onSize}
                        startDate={this.props.startDate}
                        displayCurrentDayIndicator={
                            this.props.displayCurrentDayIndicator
                        }
                        chartConteinarWidth={this.state.size.width}
                        chartScrollLeft={this.state.scrollLeft}
                        isTaskClickable={this.props.isTaskClickable}
                        currentDate={this.props.currentDate}
                    />
                </div>
            </div>
        );
    }
}

export default GanttChart;
